import { Search } from 'lucide-react'
import type { ReactNode } from 'react'
import { anchor } from '~/lib/tour/anchors'
import { cn } from '~/lib/utils'

/**
 * Page title block. The eyebrow gives a one-word location cue that survives being
 * glanced at, and the measuring-tape rule under it is the app's signature divider.
 */
export function PageHeader({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow?: string
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    // Anchored here rather than on each of twenty screens: a walkthrough step that
    // says "this is the price-check screen" wants the screen's own title block, and
    // every screen has exactly one.
    <div {...anchor('page.header')} className="mb-6">
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
        <div className="min-w-0">
          {eyebrow ? <p className="stencil mb-2 text-primary">{eyebrow}</p> : null}
          <h1 className="text-[1.625rem] leading-tight font-extrabold text-ink sm:text-[1.875rem]">
            {title}
          </h1>
          {description ? (
            <p className="mt-1.5 max-w-prose text-sm text-ink-muted">{description}</p>
          ) : null}
        </div>
        {action}
      </div>
      <div className="tick-rule mt-4" aria-hidden />
    </div>
  )
}

/** A stencilled label above a group of cards. */
export function SectionLabel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('mb-2.5 flex items-center gap-2.5', className)}>
      <span className="stencil text-ink-subtle">{children}</span>
      <span className="h-px flex-1 bg-border" />
    </div>
  )
}

export function SearchInput({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (value: string) => void
  placeholder: string
}) {
  return (
    <div {...anchor('page.search')} className="relative">
      <Search
        className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-subtle"
        aria-hidden
      />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-11 w-full rounded-control border border-border-strong bg-surface pr-3 pl-9 text-base text-ink shadow-inset transition-colors placeholder:text-ink-subtle hover:border-ink-subtle focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary sm:text-sm [&::-webkit-search-cancel-button]:appearance-none"
      />
    </div>
  )
}
